import { criterion, fromCriteria } from "@/core/check";
import type { Lesson } from "@/core/types";
import { pruefeMitMakro } from "@/office/makro/pruefung";
import { MAKRO_PIVOT } from "../makros";

export const lektionPivotChart: Lesson = {
  id: "pivotchart-zeitachse",
  title: "PivotChart und Zeitachse",
  summary: "Die Auswertung als Diagramm – und ein Filter für Zeiträume, den man sieht.",
  durationMinutes: 10,
  learningGoals: [
    "Ein PivotChart zu einer PivotTable erstellen",
    "Mit einer Zeitachse nach Monaten, Quartalen und Jahren filtern",
  ],
  steps: [
    {
      id: "pivotchart",
      title: "Das PivotChart",
      explanation: [
        "Ein PivotChart ist ein Diagramm, das direkt an einer PivotTable hängt. Zelle in der " +
          "PivotTable anklicken, „PivotTable-Analyse“ → „PivotChart“. Jede Änderung an der " +
          "PivotTable – ein anderes Feld, ein Filter – zeigt sich sofort im Diagramm, und " +
          "umgekehrt: Die Feldschaltflächen im Diagramm filtern auch die Tabelle.",
        "Für den Diagrammtyp und den Titel gilt alles aus dem Modul „Diagramme“. Die " +
          "Feldschaltflächen lassen sich für die Präsentation über „Feldschaltflächen“ → " +
          "„Alle ausblenden“ entfernen.",
      ],
      keyPoints: [
        "PivotTable-Analyse → PivotChart.",
        "Diagramm und Tabelle filtern sich gegenseitig.",
        "Feldschaltflächen für die Präsentation ausblenden.",
      ],
      task: {
        instruction: "Erstelle zu deiner PivotTable ein PivotChart.",
        hint: "Zelle in der PivotTable anklicken, PivotTable-Analyse → PivotChart → Säule.",
        macro: MAKRO_PIVOT,
        check: () =>
          pruefeMitMakro(MAKRO_PIVOT, (bericht) =>
            fromCriteria(
              [
                criterion(`Eine PivotTable ist vorhanden (gefunden: ${bericht.zahl("pivotTabellen") ?? 0})`, (bericht.zahl("pivotTabellen") ?? 0) > 0),
                criterion(`Ein PivotChart ist vorhanden (gefunden: ${bericht.zahl("pivotDiagramme") ?? 0})`, (bericht.zahl("pivotDiagramme") ?? 0) > 0),
              ],
              {
                success: "Tabelle und Diagramm arbeiten jetzt zusammen.",
                failure: "Es gibt noch kein PivotChart.",
                hint: "Ein gewöhnliches Diagramm aus den Zahlen der PivotTable zählt nicht – es muss über PivotChart entstehen.",
              },
            ),
          ),
      },
    },
    {
      id: "zeitachse",
      title: "Die Zeitachse",
      explanation: [
        "Die Zeitachse ist der Datenschnitt für Datumsfelder: „PivotTable-Analyse“ → " +
          "„Zeitachse einfügen“ → Datumsfeld wählen. Ein Balken mit Monaten erscheint; ein " +
          "Ziehen über März bis Mai filtert auf diesen Zeitraum. Rechts oben wechselt die " +
          "Stufe zwischen Jahren, Quartalen, Monaten und Tagen.",
        "Voraussetzung ist ein echtes Datumsfeld in den Quelldaten – als Text getippte " +
          "Datumsangaben bietet der Dialog gar nicht erst an.",
      ],
      keyPoints: [
        "PivotTable-Analyse → Zeitachse einfügen.",
        "Stufe: Jahre, Quartale, Monate, Tage.",
        "Nur mit echten Datumswerten.",
      ],
      task: {
        instruction: "Füge deiner PivotTable eine Zeitachse für ein Datumsfeld hinzu.",
        hint: "PivotTable-Analyse → Zeitachse einfügen → Datumsfeld ankreuzen.",
        macro: MAKRO_PIVOT,
        check: () =>
          pruefeMitMakro(MAKRO_PIVOT, (bericht) =>
            fromCriteria(
              [criterion(`Eine Zeitachse ist vorhanden (gefunden: ${bericht.zahl("zeitachsen") ?? 0})`, (bericht.zahl("zeitachsen") ?? 0) > 0)],
              {
                success: "Zeiträume filtern per Ziehen – und alle sehen, welcher Zeitraum gerade gilt.",
                failure: "Es gibt noch keine Zeitachse.",
                hint: "Fehlt das Datumsfeld im Dialog, stehen die Daten als Text in der Quelle.",
              },
            ),
          ),
      },
    },
  ],
};
